import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Form,
  Alert,
  Toast,
  ToastContainer,
  Spinner,
  Tabs,
  Tab,
} from 'react-bootstrap';
import {
  Gear,
  Lock,
  Bell,
  Eye,
  Save,
  InfoCircle,
} from 'react-bootstrap-icons';
import { useAuth } from '../contexts/AuthContext';

const SettingsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

  const [privacySettings, setPrivacySettings] = useState({
    shareData: true,
    activityTracking: false,
    showOnline: true,
  });

  const [accountSettings, setAccountSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    twoFactorAuth: false,
    sessionTimeout: 30,
  });

  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast((prev) => ({ ...prev, show: false })), 4000);
  };

  const handlePrivacyChange = (e) => {
    const { name, checked } = e.target;
    setPrivacySettings({ ...privacySettings, [name]: checked });
  };

  const handleAccountChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAccountSettings({
      ...accountSettings,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSave = () => {
    setLoading(true);
    setTimeout(() => {
      showToast('Paramètres enregistrés avec succès', 'success');
      setLoading(false);
    }, 800);
  };

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
        <Toast show={toast.show} onClose={() => setToast((prev) => ({ ...prev, show: false }))} bg={toast.variant} delay={4000} autohide>
          <Toast.Header closeButton={false}>
            <strong className="me-auto">
              {toast.variant === 'success' ? 'Succès' : toast.variant === 'danger' ? 'Erreur' : 'Information'}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="container-fluid">
        {/* En-tête */}
        <div className="mb-4">
          <h4 className="main-title mb-0 d-flex align-items-center gap-2">
            <div className="bg-primary bg-opacity-10 rounded-circle p-2">
              <Gear size={24} className="text-primary" />
            </div>
            Paramètres
          </h4>
          <p className="text-muted mb-0 mt-1">
            Gérez vos préférences de confidentialité et de compte
          </p>
        </div>

        <Row>
          <Col md={12}>
            <Card className="border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <Tabs
                defaultActiveKey="privacy"
                className="border-bottom px-3 pt-2"
                fill
              >
                <Tab
                  eventKey="privacy"
                  title={
                    <span>
                      <Lock size={16} className="me-2" />
                      Confidentialité
                    </span>
                  }
                >
                  <div className="p-4">
                    <Form>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="switch"
                          label="Partager mes données avec les professionnels de santé"
                          name="shareData"
                          checked={privacySettings.shareData}
                          onChange={handlePrivacyChange}
                        />
                        <Form.Text className="text-muted">
                          Permet aux médecins et pharmaciens d'accéder à votre dossier médical.
                        </Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Check
                          type="switch"
                          label="Autoriser le suivi d'activité"
                          name="activityTracking"
                          checked={privacySettings.activityTracking}
                          onChange={handlePrivacyChange}
                        />
                        <Form.Text className="text-muted">
                          Nous utilisons ces données pour améliorer nos services.
                        </Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Check
                          type="switch"
                          label="Afficher mon statut en ligne"
                          name="showOnline"
                          checked={privacySettings.showOnline}
                          onChange={handlePrivacyChange}
                        />
                        <Form.Text className="text-muted">
                          Permet aux autres utilisateurs de voir si vous êtes connecté.
                        </Form.Text>
                      </Form.Group>
                    </Form>
                  </div>
                </Tab>

                <Tab
                  eventKey="account"
                  title={
                    <span>
                      <Bell size={16} className="me-2" />
                      Paramètres du compte
                    </span>
                  }
                >
                  <div className="p-4">
                    <Form>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="switch"
                          label="Notifications par email"
                          name="emailNotifications"
                          checked={accountSettings.emailNotifications}
                          onChange={handleAccountChange}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Check
                          type="switch"
                          label="Notifications par SMS"
                          name="smsNotifications"
                          checked={accountSettings.smsNotifications}
                          onChange={handleAccountChange}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Check
                          type="switch"
                          label="Authentification à deux facteurs (2FA)"
                          name="twoFactorAuth"
                          checked={accountSettings.twoFactorAuth}
                          onChange={handleAccountChange}
                        />
                        <Form.Text className="text-muted">
                          Renforce la sécurité de votre compte.
                        </Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Délai d'inactivité (minutes)</Form.Label>
                        <Form.Control
                          type="number"
                          name="sessionTimeout"
                          value={accountSettings.sessionTimeout}
                          onChange={handleAccountChange}
                          min="5"
                          max="120"
                        />
                        <Form.Text className="text-muted">
                          Déconnexion automatique après cette période d'inactivité.
                        </Form.Text>
                      </Form.Group>
                    </Form>
                  </div>
                </Tab>
              </Tabs>

              <div className="p-4 border-top">
                <div className="d-flex justify-content-end">
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={loading}
                    className="rounded-pill px-4"
                  >
                    {loading ? <Spinner size="sm" className="me-1" /> : <Save size={16} className="me-1" />}
                    {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
                  </Button>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col md={12}>
            <Alert variant="info" className="border-0 rounded-3 shadow-sm" style={{ background: 'linear-gradient(135deg, #e8f4fd 0%, #d9eaff 100%)' }}>
              <div className="d-flex align-items-center gap-3">
                <InfoCircle size={24} className="text-info" />
                <div>
                  <strong className="d-block mb-1">Sécurité des données</strong>
                  <span className="small">
                    Vos informations sont protégées conformément à la réglementation en vigueur.
                    Vous pouvez modifier vos préférences à tout moment.
                  </span>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>

        <style>{`
          .card {
            transition: transform 0.2s ease, box-shadow 0.2s ease;
          }
          .card:hover {
            transform: translateY(-4px);
            box-shadow: 0 12px 20px -8px rgba(0, 0, 0, 0.15) !important;
          }
          .nav-tabs .nav-link {
            border: none;
            color: #6c757d;
            padding: 1rem 1rem;
            font-weight: 500;
            transition: all 0.2s;
          }
          .nav-tabs .nav-link:hover {
            border: none;
            color: #0d6efd;
            background: transparent;
          }
          .nav-tabs .nav-link.active {
            color: #0d6efd;
            border-bottom: 3px solid #0d6efd;
            background: transparent;
          }
        `}</style>
      </div>
    </div>
  );
};

export default SettingsPage;